import { useTranslation } from '../core/hooks/use-translation';
import { useTheme } from './use-theme';

/**
 * The one control that changes the site's appearance: the sun for the light it will switch to, the
 * moon for the dark.
 *
 * **It is a button and not a pair of radios.** There are two appearances and one of them is on, so
 * `aria-pressed` says which — while the label names the *destination*, which is what somebody
 * reaching for it wants told: pressing it on a light page gives them the dark one.
 */
export function ThemeSwitch() {
    const { t } = useTranslation();
    const { theme, setTheme } = useTheme();
    const dark = theme === 'dark';
    const label = t(dark ? 'theme.to_light' : 'theme.to_dark');

    return (
        <button
            type="button"
            onClick={() => setTheme(dark ? 'light' : 'dark')}
            aria-pressed={dark}
            aria-label={label}
            title={label}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-300 text-slate-600 transition hover:border-brand-500 hover:text-brand-600 dark:border-slate-700 dark:text-slate-300 dark:hover:border-brand-400 dark:hover:text-brand-300"
        >
            {dark ? <SunIcon /> : <MoonIcon />}
        </button>
    );
}

/*
 * Both icons are inline rather than fetched: they are two paths each, and a request for them would
 * be one more thing to fail on the screen a customer is trying to sign in from.
 */

function SunIcon() {
    return (
        <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
        >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
        </svg>
    );
}

function MoonIcon() {
    return (
        <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <path d="M20 14.2A8.2 8.2 0 0 1 9.8 4a8.2 8.2 0 1 0 10.2 10.2Z" />
        </svg>
    );
}
